import { motion } from "framer-motion";
import {
  AlertCircle,
  AlertTriangle,
  Bike,
  Building2,
  Contact2,
  Phone,
  Wallet,
} from "lucide-react";
import { Card, CardContent } from "../../../../../components/ui/card";
import { Cadastro, TelefoneInfo } from "../types";
import {
  formatCnpj,
  formatCpf,
  formatCurrency,
  formatDate,
  formatPhone,
} from "../utils/format";
import { SaldoTable } from "./saldo-table";
import { StatCard } from "./stat-card";

interface ResultsSectionProps {
  cadastros: Cadastro[];
}

export function ResultsSection({ cadastros }: ResultsSectionProps) {
  if (cadastros.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <Card className="border-destructive/50">
          <CardContent className="py-12">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
              >
                <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
              </motion.div>
              <h3 className="text-xl font-semibold text-foreground">
                Nenhum cadastro encontrado
              </h3>
              <p className="text-muted-foreground mt-2">
                Verifique os dados informados e tente novamente
              </p>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {cadastros.map((cadastro, index) => (
        <motion.div
          key={cadastro.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1, duration: 0.5 }}
        >
          <Card className="overflow-hidden border-2 hover:border-primary/50 transition-all duration-300 hover:shadow-lg">
            <CardContent className="pt-6">
              <div className="flex items-center gap-2 mb-4">
                <motion.div
                  className="h-1 w-12 bg-gradient-to-r from-primary to-secondary rounded-full"
                  initial={{ width: 0 }}
                  animate={{ width: 48 }}
                  transition={{ delay: index * 0.1 + 0.3, duration: 0.5 }}
                />
                <h3 className="text-xl font-bold text-foreground">
                  Cadastro {index + 1}
                </h3>
              </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Nome Completo"
                value={cadastro.nome_completo || "N/A"}
                icon={<Contact2 className="h-6 w-6" />}
              />
              <StatCard
                title="CPF"
                value={cadastro.cpf ? formatCpf(cadastro.cpf) : "N/A"}
                icon={<Contact2 className="h-6 w-6" />}
              />
              <StatCard
                title="Telefone"
                value={cadastro.telefone ? formatPhone(cadastro.telefone) : "N/A"}
                icon={<Phone className="h-6 w-6" />}
              />
              <StatCard
                title="OPS"
                value={cadastro.ops || "N/A"}
                icon={<Building2 className="h-6 w-6" />}
              />
            </div>

            {cadastro.cnpj && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard
                  title="CNPJ"
                  value={formatCnpj(cadastro.cnpj)}
                  icon={<Building2 className="h-6 w-6" />}
                />
              </div>
            )}

            {cadastro.telefone_info?.divergente && (
              <TelefoneDivergencia telefoneInfo={cadastro.telefone_info} />
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <StatCard
                title="Valor Total"
                value={formatCurrency(cadastro.saldo_info?.total_valor)}
                icon={<Wallet className="h-6 w-6" />}
                className={
                  cadastro.saldo_info?.possui_saldo
                    ? "border-green-500"
                    : "border-red-500"
                }
              />
              <StatCard
                title="Total sem Gorjeta"
                value={formatCurrency(
                  cadastro.saldo_info?.total_valor_sem_gorjeta
                )}
                icon={<Wallet className="h-6 w-6" />}
              />
              <StatCard
                title="Gorjeta"
                value={formatCurrency(cadastro.saldo_info?.total_gorjeta)}
                icon={<Wallet className="h-6 w-6" />}
              />
              <StatCard
                title="Status"
                value={
                  cadastro.saldo_info?.possui_saldo ? "Com Saldo" : "Sem Saldo"
                }
                icon={<Building2 className="h-6 w-6" />}
                className={
                  cadastro.saldo_info?.possui_saldo
                    ? "border-green-500"
                    : "border-red-500"
                }
              />
            </div>

            {cadastro.saldo_info?.possui_saldo &&
              cadastro.saldo_info?.registros &&
              cadastro.saldo_info.registros.length > 0 && (
                <div className="space-y-4">
                  <h4 className="text-lg font-semibold text-foreground">
                    Registros de Saldo (
                    {cadastro.saldo_info.total_registros || 0} registros - Total:{" "}
                    {formatCurrency(cadastro.saldo_info.total_valor)})
                  </h4>
                  <SaldoTable registros={cadastro.saldo_info.registros} showGorjeta />
                </div>
              )}

            {!cadastro.saldo_info?.possui_saldo && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <Card className="bg-destructive/10 border-destructive/50">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-2">
                      <motion.div
                        animate={{ scale: [1, 1.1, 1] }}
                        transition={{ duration: 2, repeat: Infinity }}
                      >
                        <AlertCircle className="h-5 w-5 text-destructive" />
                      </motion.div>
                      <p className="text-sm font-medium text-destructive">
                        {cadastro.saldo_info?.motivo ||
                          "Nenhum registro de saldo encontrado para este período"}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {cadastro.saldo_muve_info && (
              <SaldoMuveSection cadastro={cadastro} />
            )}
          </CardContent>
        </Card>
        </motion.div>
      ))}
    </div>
  );
}

function TelefoneDivergencia({ telefoneInfo }: { telefoneInfo: TelefoneInfo }) {
  return (
    <motion.div
      className="mb-6"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
    >
      <Card className="bg-amber-500/10 border-amber-500">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
            <div className="space-y-1">
              <p className="text-sm font-semibold text-amber-700">
                Divergência de telefone com romaneio_muve_garantido
              </p>
              <p className="text-sm text-amber-700">
                {telefoneInfo.motivo ||
                  "O telefone do cadastro não casa com o do romaneio garantido"}
              </p>
              <p className="text-sm text-muted-foreground">
                Cadastro:{" "}
                <span className="font-medium text-foreground">
                  {telefoneInfo.telefone_cadastro
                    ? formatPhone(telefoneInfo.telefone_cadastro)
                    : "N/A"}
                </span>
              </p>
              <p className="text-sm text-muted-foreground">
                Romaneio:{" "}
                <span className="font-medium text-foreground">
                  {telefoneInfo.telefones_romaneio.length > 0
                    ? telefoneInfo.telefones_romaneio
                        .map(
                          (t) =>
                            `${formatPhone(t.telefone)} (${formatDate(
                              t.ultima_data
                            )})`
                        )
                        .join(" · ")
                    : "N/A"}
                </span>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

function SaldoMuveSection({ cadastro }: { cadastro: Cadastro }) {
  const saldoMuve = cadastro.saldo_muve_info;
  if (!saldoMuve) return null;

  return (
    <motion.div
      className="mt-8 space-y-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.4 }}
    >
      <div className="flex items-center gap-2">
        <Bike className="h-5 w-5 text-primary" />
        <h4 className="text-lg font-semibold text-foreground">Saldo Muve</h4>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          title="Valor Total Muve"
          value={formatCurrency(saldoMuve.total_valor)}
          icon={<Wallet className="h-6 w-6" />}
          className={
            saldoMuve.possui_saldo ? "border-green-500" : "border-red-500"
          }
        />
        <StatCard
          title="Status Muve"
          value={saldoMuve.possui_saldo ? "Com Saldo" : "Sem Saldo"}
          icon={<Building2 className="h-6 w-6" />}
          className={
            saldoMuve.possui_saldo ? "border-green-500" : "border-red-500"
          }
        />
      </div>

      {saldoMuve.possui_saldo &&
      saldoMuve.registros &&
      saldoMuve.registros.length > 0 ? (
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {saldoMuve.total_registros || 0} registros - Total:{" "}
            {formatCurrency(saldoMuve.total_valor)}
          </p>
          <SaldoTable registros={saldoMuve.registros} showVencimento />
        </div>
      ) : (
        <Card className="bg-muted/50">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                {saldoMuve.erro ||
                  saldoMuve.motivo ||
                  "Nenhum registro de Muve encontrado para este período"}
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </motion.div>
  );
}
